import { IconTypes } from '@genial/components';
import { IconographyTypes } from '@genial/icons';
import { BackendError, CustomerFinancialSuccessPagedListResponse, Service } from '@genial/services';
import { CustomerEntity, MetaResponse } from '@genial/services/dist/models';
import { CustodyCustomerV4Entity } from '@genial/services/src';
import { Utils } from '@genial/utils';
import { selectorFamily, selector } from 'recoil';

import { blankMetaResponse } from '../../utils/blankMetaResponse';
import { advisorIdToApisSelector, partnerIdToApisSelector } from '../master/selector';
import { genericListSortingState } from '../sorting';

import {
  customerBySearchTermParamsState,
  customerItemState,
  customerParamsState,
  customerStatusState,
  isExportingPdfForCustomers,
  listAllCustomersState,
  listCustomersBySearchTermState,
} from './atom';
import { marketTypeMap, productsWithTerm, screenName } from './constants';
import { normalizeV4Clients, normalizeV4ClientsUngrouped } from './normalize';
import {
  CustomerItem,
  CustomerStatus,
  CustomerParams,
  CustomerBySearchTermParams,
  CustomerFinancialResponseNormalized,
  CustomerWalletPointsNormalized,
  CustomerV4FinancialResponseNormalized,
  CustomerGlobalSearchResponseNormalized,
} from './types';

export const customerFinancialQuery = selectorFamily<
  | {
      meta: MetaResponse;
      data: CustomerFinancialResponseNormalized[];
      notFound?: boolean;
    }
  | null
  | undefined,
  { searchTerm?: string; page: number; limit?: number; isAutocomplete?: boolean }
>({
  key: 'Customer/Financial/Query',
  get:
    ({ page, searchTerm, limit, isAutocomplete = false }) =>
    async ({ get }) => {
      let customers: CustomerFinancialSuccessPagedListResponse;
      const advisorSelected = get(advisorIdToApisSelector);
      const partnerSelected = get(partnerIdToApisSelector);

      if (searchTerm) {
        try {
          customers = await Service.Customer.V2.getBySearchTerm({
            searchTerm,
            page,
            limit,
            screenName,
            advisorId: advisorSelected,
            partnerId: partnerSelected,
            isFinancial: !isAutocomplete,
          });
        } catch (error) {
          const backendError = error as BackendError;

          if (backendError.statusCode === 404) {
            return { data: [], meta: blankMetaResponse(), notFound: true };
          }

          throw error;
        }
      } else {
        try {
          customers = await Service.Customer.V2.getAllFinancial({
            page,
            limit,
            screenName,
            advisorId: advisorSelected,
            partnerId: partnerSelected,
            isFinancial: !isAutocomplete,
          });
        } catch (error) {
          const backendError = error as BackendError;

          if (backendError.statusCode === 404) {
            return { data: [], meta: blankMetaResponse() };
          }

          throw error;
        }
      }

      const normalizedClients: CustomerFinancialResponseNormalized[] =
        customers.data?.map((item) => {
          const statusNormalized = item.status.statusName?.toUpperCase() ?? '--';
          return {
            ...item,
            customerNameNormalized: item.fullName ?? '--',
            suitabilityColorNormalized: Utils.Suitability.getCorrectColor(
              item.suitabilityProfileName || ''
            ),
            cpfCnpjNormalized: Utils.Mask.CpfOrCnpj(item.cpfCnpjFormatted),
            personTypeNormalized: item.personTypeName ?? '--',
            sinacorAccountNormalized: `${item.accountNumber}-${item.accountDigit}`,
            netAmount: item.balance,
            netAmountUpdatedAt: item.balanceUpdatedAt,
            netAmountNormalized: Utils.Mask.Money(item.balance),
            netAmountUpdatedAtNormalized: item.balanceUpdatedAt
              ? Utils.Mask.DateFormat(item.balanceUpdatedAt, 'dd/MM/yyyy')
              : '--',
            accountDateNormalized: Utils.Mask.DateFormat(item.createdAt, 'dd/MM/yyyy'),
            accountStatusNormalized: statusNormalized,
            accountStatusTextColorNormalized:
              statusNormalized !== 'ATIVO'
                ? Utils.ColorToken.fromColor(item.status.iconColor)
                : 'neutral.black',
            accountStatusIconColorNormalized: Utils.ColorToken.fromColor(item.status.iconColor),
            accountStatusIconNormalized: (item.status.icon ?? 'Info') as IconographyTypes.Option,
            accountStatusIconSizeNormalized: (item.status.iconSize as IconTypes.Sizes) ?? 'xxxs',
          };
        }) || [];

      const hasNext = searchTerm
        ? false
        : normalizedClients.length >= customers.meta.pagination.limit;

      return {
        data: normalizedClients,
        meta: { pagination: { ...customers.meta.pagination, hasNext } },
      };
    },
});

export const groupedCustomerFinancialQuery = selectorFamily<
  | {
      meta: MetaResponse;
      data: CustomerV4FinancialResponseNormalized[];
    }
  | null
  | undefined,
  { searchTerm: string; page: number; limit?: number }
>({
  key: 'groupedCustomerFinancialQuery',
  get:
    ({ page, searchTerm, limit }) =>
    async ({ get }) => {
      try {
        const advisorSelected = get(advisorIdToApisSelector);
        const partnerSelected = get(partnerIdToApisSelector);
        const sorting = get(genericListSortingState('customers'));

        const serviceToCall = searchTerm
          ? Service.Customer.V4.getBySearchTerm
          : Service.Customer.V4.getAllFinancial;

        const { data, meta } = await serviceToCall({
          page,
          limit,
          screenName,
          advisorId: advisorSelected,
          partnerId: partnerSelected,
          searchTerm,
          sortBy: sorting.sortKey || undefined,
          sortDir: sorting.sortDirection || undefined,
        });

        const normalizedClients = normalizeV4Clients(data || []);

        return {
          data: normalizedClients,
          meta,
        };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], meta: blankMetaResponse() };
        }

        throw error;
      }
    },
});

export const customerAutocompleteQuery = selectorFamily<
  | {
      meta: MetaResponse;
      data: CustomerFinancialResponseNormalized[];
    }
  | null
  | undefined,
  { searchTerm?: string }
>({
  key: 'Customer/Autocomplete/Query',
  get:
    ({ searchTerm }) =>
    async ({ get }) => {
      if (!searchTerm || searchTerm.length < 3) {
        return { data: [], meta: blankMetaResponse() };
      }

      return get(customerFinancialQuery({ searchTerm, page: 1, limit: 5, isAutocomplete: true }));
    },
});

export const customerGlobalSearchQuery = selectorFamily<
  | {
      meta: MetaResponse;
      data: CustomerGlobalSearchResponseNormalized[];
    }
  | null
  | undefined,
  { searchTerm?: string }
>({
  key: 'Customer/GlobalSearch/Query',
  get:
    ({ searchTerm }) =>
    async ({ get }) => {
      if (!searchTerm || searchTerm.length < 3) {
        return { data: [], meta: blankMetaResponse() };
      }

      const customers = get(groupedCustomerFinancialQuery({ searchTerm, page: 1, limit: 5 }));

      if (!customers || !customers.data) {
        return { data: [], meta: blankMetaResponse() };
      }

      return {
        data: normalizeV4ClientsUngrouped(customers.data),
        meta: customers.meta,
      };
    },
});

export const changeCustomerStatus = selector({
  key: 'changeCustomerStatus',
  set: ({ set }, newStatus) => {
    const data = newStatus as CustomerStatus;
    set(customerStatusState, data);
  },
  get: ({ get }) => {
    const currentStatus = get(customerStatusState);
    return currentStatus.status;
  },
});

export const customerItem = selector({
  key: 'customerItem',
  set: ({ set, get }, newValue) => {
    const customer = get(customerItemState);
    const data = {
      current: newValue,
      previous: customer.current,
    } as CustomerItem;
    set(customerItemState, data);
  },
  get: ({ get }) => {
    const item = get(customerItemState);
    return item;
  },
});

export const hasCustomerItem = selector({
  key: 'hasCustomerItem',
  get: ({ get }) => {
    const customer = get(customerItemState);
    return Object.keys(customer.current)?.length > 0;
  },
});

export const customerParams = selector({
  key: 'customerParams',
  set: ({ set, get }, newValue) => {
    const currentCustomer = get(customerParamsState);
    const value = newValue as Partial<CustomerParams>;

    const customer = { ...currentCustomer, ...value };

    return set(customerParamsState, customer);
  },
  get: ({ get }) => {
    const item = get(customerParamsState);
    return item;
  },
});

export const customerBySearchTermParams = selector({
  key: 'customerBySearchTermParams',
  set: ({ set, get }, newValue) => {
    const currentCustomerBySearchTerm = get(customerBySearchTermParamsState);
    const value = newValue as Partial<CustomerBySearchTermParams>;

    const customerBySearchTerm = { ...currentCustomerBySearchTerm, ...value };

    return set(customerBySearchTermParamsState, customerBySearchTerm);
  },
  get: ({ get }) => {
    const item = get(customerBySearchTermParamsState);
    return item;
  },
});

export const customersAllQuery = selector({
  key: 'NewApplication/customersAllQuery',
  get: async ({ get }) => {
    try {
      const customerParamsData = get(customerParamsState);
      const advisorSelected = get(advisorIdToApisSelector);
      const partnerSelected = get(partnerIdToApisSelector);

      const { data, meta } = await Service.Customer.V4.getAllCockpit({
        ...customerParamsData,
        advisorId: advisorSelected,
        partnerId: partnerSelected,
        screenName,
      });

      const normalizedData = data?.map((item) => item.customer) || [];

      const hasNext = normalizedData.length >= meta.pagination.limit;

      return { data: normalizedData, meta: { pagination: { ...meta.pagination, hasNext } } };
    } catch (error) {
      const backendError = error as BackendError;

      if (backendError.statusCode === 404) {
        return { data: [], meta: blankMetaResponse() };
      }

      throw error;
    }
  },
});

export const customersBySearchTermQuery = selector({
  key: 'NewApplication/customersBySearchTermQuery',
  get: async ({ get }) => {
    const customerBySearchTermParamsData = get(customerBySearchTermParamsState);
    const advisorSelected = get(advisorIdToApisSelector);
    const partnerSelected = get(partnerIdToApisSelector);

    if (customerBySearchTermParamsData.skipEndpointCall) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { skipEndpointCall, ...restCustomerBySearchTermParamsData } =
      customerBySearchTermParamsData;

    const { data, meta } = await Service.Customer.V4.getCockpitBySearchTerm({
      ...restCustomerBySearchTermParamsData,
      screenName,
      advisorId: advisorSelected,
      partnerId: partnerSelected,
      limit: 100,
    });

    const normalizedData = data?.map((item) => item.customer) || [];

    const hasNext = false;

    return { data: normalizedData, meta: { pagination: { ...meta.pagination, hasNext } } };
  },
});

export const updateListAllCustomersSelector = selector({
  key: 'updateListAllCustomersSelector',
  set: ({ set }, newValue) => {
    const data = newValue as CustodyCustomerV4Entity[];
    return set(listAllCustomersState, data);
  },
  get: ({ get }) => {
    const item = get(listAllCustomersState);
    return item;
  },
});

export const updateListCustomersBySearchTermSelector = selector({
  key: 'updateListCustomersBySearchTermSelector',
  set: ({ set }, newValue) => {
    const data = newValue as CustodyCustomerV4Entity[];
    return set(listCustomersBySearchTermState, data);
  },
  get: ({ get }) => {
    const item = get(listCustomersBySearchTermState);
    return item;
  },
});

export const updateIsExportingPdfForCustomer = selector({
  key: 'updateIsExportingPdfForCustomer',
  set: ({ set }, newValue) => {
    const isExporting = newValue as boolean;
    return set(isExportingPdfForCustomers, isExporting);
  },
  get: ({ get }) => {
    const item = get(isExportingPdfForCustomers);
    return item;
  },
});

export const customerByAccountNumberQuery = selectorFamily<
  { data: CustomerEntity },
  { accountNumber: number; currentScreen?: string }
>({
  key: 'customers/customerByAccountNumberQuery',
  get: ({ accountNumber, currentScreen }) => {
    return async ({ get }) => {
      const advisorSelected = get(advisorIdToApisSelector);
      const partnerSelected = get(partnerIdToApisSelector);

      const { data } = await Service.Customer.V1.getByAccountNumber({
        screenName: currentScreen || screenName,
        accountNumber,
        advisorId: advisorSelected,
        partnerId: partnerSelected,
      });

      return { data: data.customer };
    };
  },
});

export const customerHasTermAvailableQuery = selectorFamily<
  boolean,
  { accountId: number; marketType: string; productIdForTerm: number }
>({
  key: 'customerHasTermAvailable',
  get: ({ accountId, marketType, productIdForTerm }) => {
    return async () => {
      const productMarketHasTerm = productsWithTerm.includes(marketType);

      if (!productMarketHasTerm) {
        return true;
      }

      const marketTypeMapped = marketTypeMap.get(marketType) || marketType;

      const { data } = await Service.Agreement.V1.getTermsByStatusMarketTypeAccountIdProductId({
        accountId,
        marketType: marketTypeMapped,
        productId: productIdForTerm,
        screenName: 'aplicacao-meus-clientes-direto',
      });

      return data.hasSigned;
    };
  },
});

export const customerWalletPointsQuery = selectorFamily<
  CustomerWalletPointsNormalized | null | undefined,
  { accountNumber: number }
>({
  key: 'customer/customerWalletPointsQuery',
  get: ({ accountNumber }) => {
    return async ({ get }) => {
      const advisorSelected = get(advisorIdToApisSelector);
      const partnerSelected = get(partnerIdToApisSelector);

      const { data } = await Service.Customer.V3.getRiskWalletByAccountNumber({
        accountNumber,
        screenName,
        advisorId: advisorSelected,
        partnerId: partnerSelected,
      });

      return {
        currentPoints: data.walletRiskLimit.toFixed(2),
        limitPoints: data.profileRiskPoint.toFixed(2),
      } as CustomerWalletPointsNormalized;
    };
  },
});

export const customerSuitabilityMismatchQuery = selectorFamily<
  { isMismatchedAndShouldNotify: boolean } | null | undefined,
  { accountNumber: number }
>({
  key: 'customer/customerSuitabilityMismatchQuery',
  get: ({ accountNumber }) => {
    return async ({ get }) => {
      try {
        const advisorSelected = get(advisorIdToApisSelector);
        const partnerSelected = get(partnerIdToApisSelector);

        const { data } =
          await Service.Customer.V3.getRiskWalletByOutOfComplianceNotificationsAccountNumber({
            accountNumber,
            screenName,
            advisorId: advisorSelected,
            partnerId: partnerSelected,
          });

        return { isMismatchedAndShouldNotify: !data.isNotified };
      } catch (_) {
        return { isMismatchedAndShouldNotify: false };
      }
    };
  },
});
